/**
 * DeliveryLog — the Delivery tab of the Messaging rail: the send log. One row
 * per notification that went out across channels and sources (flow / campaign /
 * ticket-tier / manual / on-site), searchable, filterable, sortable and
 * paginated. Click a row for the per-recipient event history + rendered message.
 *
 * Route: `messaging.delivery` · capability: delivery.read.
 * Data: the live notification-history ops (listMessageHistory / getMessageHistoryDetail).
 */
import { useEffect, useState, type ReactNode } from "react";
import { DataTable, Drawer, Dropdown, MultiSelect, PageState, Pagination, SegmentedControl, alpha, Btn, Card, Icon, Input, Pill, T, tx, type TableColumn } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { ChGlyph, ChannelChip, MSG_CHANNEL } from "./messaging-shared";
import { messagingTabs, useMessagingCounts } from "./messaging-tabs";
import {
  DELIV_EVENTS, DELIV_SOURCES, SOURCE_FILTER_LABELS, DELIV_ROW_STATUSES,
  lastEvent, delivStatus, hasMissingTokens, missingTokensOf, sortDeliveries, type DeliveryRow,
} from "./delivery-data";
import { fetchMessageHistory, fetchMessageHistoryDetail, itemToRow, detailToRow } from "./message-history-api";
import { exportDeliveryCsv } from "@v0y4ge/api-client";
import { useT, t as i18nT } from "../../lib/i18n";

const IconMap = Icon as unknown as Record<string, (p?: { size?: number }) => ReactNode>;
const delivPill = (type: string) => { const e = DELIV_EVENTS[type] || DELIV_EVENTS.send; return <Pill tone={e.tone as any} dot>{e.label}</Pill>; };

/* How to READ the row, not how it ended (CC 2026-09-03 §2). `dispatch` rows show
   the bytes the customer received; `template` rows show today's template with
   tokens unresolved by design. Deliberately not a status colour pair: amber here
   means "read this differently", never "something failed". */
/* A hole in what a customer actually received — danger, not "read this
 * differently": the amber content chip says which content is shown, this says
 * the send itself was wrong. Ignored on `template` rows, where an unresolved
 * token is expected (CC 2026-09-03 §6). The count comes from the timeline once
 * the drawer has fetched it; on the list only the boolean is on the wire. */
function MissingTokensChip({ row }: { row: DeliveryRow }) {
  if (!hasMissingTokens(row)) return null;
  const names = missingTokensOf(row);
  const title = names.length
    ? `${names.length} ${names.length === 1 ? "token" : "tokens"} rendered empty: ${names.join(", ")}`
    : i18nT("messaging.deliveryLog.missingTokensTitle", "Some tokens rendered empty — this recipient saw gaps. Open the row for the names.");
  return <span title={title}><Pill tone="danger">{names.length
    ? i18nT("messaging.deliveryLog.missingTokensCount", "{n} tokens missing").replace("{n}", String(names.length))
    : i18nT("messaging.deliveryLog.missingTokens", "Tokens missing")}</Pill></span>;
}

function ContentSourceChip({ row }: { row: DeliveryRow }) {
  if (!row.contentSource) return null;
  return row.contentSource === "template"
    ? <Pill tone="warn">{i18nT("messaging.deliveryLog.fromTemplate", "From template")}</Pill>
    : <Pill tone="success">{i18nT("messaging.deliveryLog.asSent", "As sent")}</Pill>;
}

function SourceTag({ source }: { source: { kind: string; label: string } }) {
  const s = DELIV_SOURCES[source.kind] || DELIV_SOURCES.manual;
  const I = IconMap[s.icon] || IconMap.send;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: T.n1 }}>
      <span style={{ width: 20, height: 20, borderRadius: 5, background: alpha(s.color, "18"), color: s.color, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{I({ size: 11 })}</span>
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{source.label}</span>
    </span>
  );
}

interface SelOpt { value: string; label: string; }

/* DSelect / DMultiSelect are GONE (DS pass 2026-08-31). Both carried a ui-raw
 * note claiming jrni-ui could not express them; both notes were refuted against
 * the current barrel:
 *  · DSelect  — "Dropdown has no width/align". Dropdown sizes its menu to the
 *    TRIGGER width and takes wrapper `style`, so `style={{ width: 84 }}` gives
 *    the rows-per-page control its exact box and a menu that can't overhang;
 *    there was never anything for `align` to fix.
 *  · DMultiSelect — "MultiSelect renders picks as chips, not All/1-label/N-noun".
 *    `chipLimit={0}` + `summaryLabel` + `placeholder` reproduce that trigger text
 *    exactly, and both gain the listbox a11y and keyboard handling the hand-rolls
 *    lacked.
 * `DFilterSelect` below is a thin i18n/label wrapper, not a re-implementation. */
function DFilterSelect({ value, options, onChange, allLabel, noun, width = 176 }: { value: string[]; options: SelOpt[]; onChange: (v: string[]) => void; allLabel: string; noun: string; width?: number }) {
  return (
    <MultiSelect<string>
      options={options} values={value || []} onChange={onChange}
      placeholder={allLabel} chipLimit={0} width={Math.max(width, 200)}
      style={{ width, flexShrink: 0 }} aria-label={allLabel}
      summaryLabel={(n) => (n === 1 ? ((options.find((o) => o.value === (value || [])[0]) || { label: allLabel }).label) : `${n} ${noun}`)} />
  );
}

/* Pagination shell — the "Showing X–Y of Z" status and the rows-per-page
 * control are the consumer's job (Pagination's own docs: "Page-size selector /
 * jump-to-input are deferred to the consumer"). The PAGE BUTTONS, which the old
 * hand-rolled PgBtn + _pageList owned, are now jrni-ui Pagination with
 * showStatus={false} — including its ellipsis collapsing, aria-current="page"
 * and Previous/Next aria-labels. */
function DeliveryPagination({ page, pageCount, total, start, end, pageSize, onPage, onPageSize }: { page: number; pageCount: number; total: number; start: number; end: number; pageSize: number; onPage: (p: number) => void; onPageSize: (n: number) => void }) {
  const t = useT();
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 16px", borderTop: `1px solid ${T.border}`, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12.5, color: T.soft }}>{t("messaging.deliveryLog.showing", "Showing")}{" "}<b style={{ color: T.n1 }}>{total ? start : 0}–{end}</b> {t("messaging.deliveryLog.of", "of")}{" "}<b style={{ color: T.n1 }}>{total}</b></span>
      <Pagination size="sm" showStatus={false} totalPages={pageCount} currentPage={page} onPageChange={onPage} />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12.5, color: T.soft }}>{t("messaging.deliveryLog.rowsPerPage", "Rows per page")}</span>
        <Dropdown<string>
          value={String(pageSize)} onChange={(v) => onPageSize(Number(v))}
          style={{ width: 84 }} aria-label={t("messaging.deliveryLog.rowsPerPage", "Rows per page")}
          options={/* ui-config — fixed page-size steps */[{ value: "10", label: "10" }, { value: "25", label: "25" }, { value: "50", label: "50" }]} />
      </div>
    </div>
  );
}

export default function DeliveryLog() {
  const t = useT();
  const nav = useNav();
  const counts = useMessagingCounts();
  const [q, setQ] = useState("");
  const [chan, setChan] = useState<string[]>([]);
  const [status, setStatus] = useState<string[]>([]);
  const [src, setSrc] = useState<string[]>([]);
  /* "With missing tokens" — cheap because the row flag is on the list payload.
     It is the one filter an operator needs after a bad send. */
  const [mtOnly, setMtOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" }>({ key: "latest", dir: "desc" });
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const [all, setAll] = useState<DeliveryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DeliveryRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  /* Bumped by PageState's Retry — re-runs the read below. */
  const [reloadKey, setReloadKey] = useState(0);

  // Load the send log from listMessageHistory (limit 200 covers the full seed;
  // client-side filter/sort/paginate below stays as-is on the fetched set).
  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    fetchMessageHistory({ limit: 200 })
      .then((resp) => { if (live) { setAll((resp.data || []).map(itemToRow)); setLoading(false); } })
      .catch((e) => { if (live) { setError(e?.message || "Failed to load the notification history."); setLoading(false); } });
    return () => { live = false; };
  }, [reloadKey]);

  // Full detail (timeline + rendered body) on drawer open.
  useEffect(() => {
    if (!openId) { setDetail(null); setDetailLoading(false); return; }
    let live = true;
    setDetail(null); setDetailLoading(true);
    fetchMessageHistoryDetail(openId)
      .then((d) => { if (live) { setDetail(detailToRow(d)); setDetailLoading(false); } })
      .catch(() => { if (live) setDetailLoading(false); });
    return () => { live = false; };
  }, [openId]);
  const baseFiltered = all.filter((d) => {
    if (chan.length && !chan.includes(d.channel)) return false;
    if (status.length && !status.includes(delivStatus(d))) return false;
    if (mtOnly && !hasMissingTokens(d)) return false;
    if (q.trim()) {
      const hay = `${d.recipient.name} ${d.recipient.to} ${d.sender.name} ${d.subject || d.text || ""} ${d.source.label}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });
  const filtered = src.length === 0 ? baseFiltered : baseFiltered.filter((d) => src.includes(d.source.kind));
  const rows = sortDeliveries(filtered, sort);

  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(1); }, [q, chan.join(), status.join(), src.join(), mtOnly, pageSize, sort.key, sort.dir]);
  const safePage = Math.min(page, pageCount);
  const startIdx = (safePage - 1) * pageSize;
  const paged = rows.slice(startIdx, startIdx + pageSize);

  const listRow = openId ? all.find((d) => d.id === openId) ?? null : null;
  const open = detail ?? listRow;
  const chanOpts = Array.from(new Set(all.map((d) => d.channel)));
  const chanSelOpts: SelOpt[] = chanOpts.map((c) => ({ value: c, label: MSG_CHANNEL(c).name }));
  const statusSelOpts: SelOpt[] = DELIV_ROW_STATUSES.map((k) => ({ value: k, label: (DELIV_EVENTS[k] || DELIV_EVENTS.send).label }));
  const srcKinds = ["flow", "campaign", "trigger", "ticketgroup", "onsite", "manual"].filter((k) => all.some((d) => d.source.kind === k));
  const srcSelOpts: SelOpt[] = srcKinds.map((k) => ({ value: k, label: SOURCE_FILTER_LABELS[k] }));
  const filtersActive = q.trim() !== "" || chan.length > 0 || status.length > 0 || src.length > 0 || mtOnly;
  const resetFilters = () => { setQ(""); setChan([]); setStatus([]); setSrc([]); setMtOnly(false); };
  // Addendum item 9 (#262): Export awaits the real exportDeliveryCsv and
  // downloads the returned CSV — success feedback only from its receipt.
  const [exporting, setExporting] = useState(false);
  const doExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await exportDeliveryCsv({ format: "csv", q: q.trim() || undefined }) as unknown as Record<string, unknown>;
      const body = (r.csv ?? r.content ?? r.body ?? (typeof r === "string" ? r : null)) as string | null;
      const url = typeof r.downloadUrl === "string" ? r.downloadUrl : body != null ? URL.createObjectURL(new Blob([body], { type: "text/csv" })) : null;
      if (!url) throw new Error("export returned no CSV payload");
      const a = document.createElement("a");
      a.href = url; a.download = String(r.filename || "delivery-log.csv"); a.rel = "noopener"; a.style.display = "none";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      if (!String(r.downloadUrl || "")) setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast({ tone: "success", title: t("messaging.deliveryLog.exportDownloaded", "Export downloaded"), subtitle: String(r.filename || "delivery-log.csv") });
    } catch (e) {
      toast({ tone: "danger", title: t("messaging.deliveryLog.exportFailed", "Export failed"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally { setExporting(false); }
  };

  const delivColumns: TableColumn<DeliveryRow>[] = [
    { id: "recipient", header: t("messaging.deliveryLog.recipient", "Recipient"), sortable: true, cell: (d) => (
      <><div style={{ fontWeight: 600, color: T.n1 }}>{d.recipient.name}</div>
        <div style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{d.recipient.to}</div></>
    ) },
    { id: "channel", header: t("messaging.deliveryLog.channel", "Channel"), sortable: true, minWidth: 100, cell: (d) => <ChannelChip id={d.channel} size="sm" /> },
    { id: "message", header: t("messaging.deliveryLog.message", "Message"), sortable: true, cell: (d) => (
      <div style={{ maxWidth: 280, display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
        <div style={{ maxWidth: "100%", color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.subject || d.text}</div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}><ContentSourceChip row={d} /><MissingTokensChip row={d} /></div>
      </div>
    ) },
    { id: "sender", header: t("messaging.deliveryLog.sender", "Sender"), sortable: true, cell: (d) => (
      <><div style={{ color: T.n1 }}>{d.sender.name}</div><div style={{ fontSize: 11.5, color: T.soft }}>{d.sender.from}</div></>
    ) },
    { id: "source", header: t("messaging.deliveryLog.source", "Source"), sortable: true, cell: (d) => <div style={{ maxWidth: 200 }}><SourceTag source={d.source} /></div> },
    /* The badge is the row's STATUS, not the type of its latest event: a
       scheduled reminder carries a `send` event ("Queued for delivery") while
       its status is `queued`, and reading the event type rendered it as "Sent".
       A queued row has no sent time; it shows its PLANNED time when the wire
       carries one (`scheduledFor`, JAI-1377) and no time at all until then —
       stating the creation instant would state a time that is not the plan. */
    { id: "latest", header: t("messaging.deliveryLog.statusColumn", "Status"), sortable: true, minWidth: 160, cell: (d) => {
      const st = delivStatus(d);
      const queued = st === "queued";
      const when = queued ? (d.plannedAt ? `${t("messaging.deliveryLog.plannedFor", "Planned")} ${d.plannedAt}` : "") : lastEvent(d).at;
      return <>{delivPill(st)}{when ? <div style={{ fontSize: 11, color: T.soft, marginTop: 3 }}>{when}</div> : null}</>;
    } },
  ];
  /* Table's toggleSort starts every NEW column ascending; the hand-rolled SortTh
     started "latest" descending (newest first, which is the only useful default
     for a send log). Preserved here rather than silently changed. */
  const applySort = (next: { columnId: string; direction: "asc" | "desc" }) =>
    setSort((s) => (s.key !== next.columnId && next.columnId === "latest" ? { key: "latest", dir: "desc" } : { key: next.columnId, dir: next.direction }));

  return (
    <Screen rail="messaging" tabs={messagingTabs(nav, "delivery", counts)} activeTab="delivery">
      <PageHeader
        title={t("messaging.deliveryLog.messaging", "Messaging")}
        subtitle={t("messaging.deliveryLog.deliveryEveryNotificationSentAcrossChannelsWithFullPerRecipientEventHist", "Delivery — every notification sent across channels, with full per-recipient event history.")}
        actions={<>
          {can("reports.export") ? <Btn variant="secondary" icon={<Icon.download size={14} />} disabled={exporting} onClick={() => void doExport()}>{exporting ? t("messaging.deliveryLog.exporting", "Exporting…") : t("common.actions.export", "Export")}</Btn> : null}
        </>}
      />
      <DataSourceStrip screen="messaging-delivery" ops={["listMessageHistory", "getMessageHistoryDetail"]} />
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", paddingTop: 2, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <Input placeholder={t("messaging.deliveryLog.findByRecipientSenderSubject", "Find by recipient, sender, subject…")} value={q} onChange={(e) => setQ(e.target.value)} style={{ width: "100%" }} />
          </div>
          <DFilterSelect value={src} options={srcSelOpts} onChange={setSrc} allLabel="All sources" noun="sources" width={170} />
          <DFilterSelect value={chan} options={chanSelOpts} onChange={setChan} allLabel="All channels" noun="channels" width={158} />
          <DFilterSelect value={status} options={statusSelOpts} onChange={setStatus} allLabel="All statuses" noun="statuses" width={166} />
          {/* ui-raw: chip toggle in a filter row — ChipToggleGroup's pattern (group 6) */}
          <button type="button" onClick={() => setMtOnly(!mtOnly)} data-testid="delivery-missing-tokens-filter"
            aria-pressed={mtOnly}
            style={{ minHeight: 32, padding: "0 11px", borderRadius: 999, border: `1px solid ${mtOnly ? T.red1 : T.border}`, background: mtOnly ? T.red2 : T.white, color: mtOnly ? T.red1 : T.n2, fontFamily: T.font, fontSize: 12.5, fontWeight: mtOnly ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap" }}>
            {t("messaging.deliveryLog.withMissingTokens", "With missing tokens")}
          </button>
          {filtersActive ? (
            <Btn type="button" variant="link" size="sm" onClick={resetFilters} icon={<Icon.x size={12} />}
              style={{ flexShrink: 0, height: "auto", gap: 6, padding: "6px 10px", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>
              {t("messaging.deliveryLog.clearFilters", "Clear filters")}
            </Btn>
          ) : null}
        </div>

        {/* States come from the library now: PageState owns the error-before-empty
            branch order and the table skeleton; the three hand-rolled <tr> states
            (a centred "Loading…", a red error line, a "no match" line) are gone.
            `onRetry` is NEW behaviour — the failed read previously had no recovery
            affordance at all; it re-runs the same listMessageHistory read. */}
        <PageState noun={t("messaging.deliveryLog.nounNotifications", "notifications")}
          loading={loading} error={error} empty={total === 0} filtered={filtersActive}
          skeleton="table" skeletonColumns={6}
          onRetry={() => setReloadKey((k) => k + 1)}
          onClearFilters={filtersActive ? resetFilters : undefined}
          icon={<Icon.send size={20} />}>
          <Card style={{ padding: 0, marginTop: 14 }}>
            {/* DataTable's own wrapper border/radius is suppressed — the Card is
                already the surface, and two borders would nest. */}
            <DataTable<DeliveryRow>
              rows={paged} getRowId={(d) => d.id} columns={delivColumns}
              sort={{ columnId: sort.key, direction: sort.dir }}
              onSortChange={applySort}
              onRowClick={(d) => setOpenId(d.id)}
              style={{ border: "none", borderRadius: 0, background: "transparent" }}
              testid="delivery-table" />
            {total > 0 ? (
              <DeliveryPagination page={safePage} pageCount={pageCount} total={total}
                start={startIdx + 1} end={Math.min(startIdx + pageSize, total)} pageSize={pageSize}
                onPage={setPage} onPageSize={setPageSize} />
            ) : null}
          </Card>
        </PageState>
      </div>

      {open ? <DeliveryDetailSheet d={open} loading={detailLoading} onClose={() => setOpenId(null)} /> : null}
    </Screen>
  );
}

function DeliveryDetailSheet({ d, loading, onClose }: { d: DeliveryRow; loading?: boolean; onClose: () => void }) {
  const t = useT();
  const [showSource, setShowSource] = useState(false);
  /* Body scroll-lock + Escape-to-close were hand-rolled here; Drawer owns both
     (plus the focus trap the hand-roll never had). */

  const ch = MSG_CHANNEL(d.channel);
  const credBadge = d.credentialSource === "platform_fallback"
    ? <span title={t("messaging.deliveryLog.sentWithThePlatformSSharedCredentials", "Sent with the platform's shared credentials")} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: T.orange1, background: alpha(T.orange1, 14), borderRadius: 4, padding: "1px 6px", verticalAlign: "middle" }}>{t("messaging.deliveryLog.platformKey", "Platform key")}</span>
    : d.credentialSource === "tenant"
      ? <span title={t("messaging.deliveryLog.sentWithYourOwnProviderCredentials", "Sent with your own provider credentials")} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: T.green1, background: alpha(T.green1, 14), borderRadius: 4, padding: "1px 6px", verticalAlign: "middle" }}>{t("messaging.deliveryLog.ownKey", "Own key")}</span>
      : null;
  const providerLabel = d.provider ? d.provider.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null;
  const meta: { label: string; value: ReactNode; sub?: string }[] = [
    { label: t("messaging.deliveryLog.to", "To"), value: <span style={{ fontFamily: T.fontMono }}>{d.recipient.to}</span>, sub: d.recipient.name },
    { label: t("messaging.deliveryLog.from", "From"), value: d.sender.name, sub: d.sender.from },
    { label: t("messaging.deliveryLog.source", "Source"), value: <SourceTag source={d.source} /> },
    { label: t("messaging.deliveryLog.messageId", "Message ID"), value: <span style={{ fontFamily: T.fontMono, fontSize: 12 }}>{d.displayId || d.id}</span> },
  ];
  if (providerLabel) meta.push({ label: t("messaging.deliveryLog.provider", "Provider"), value: <span>{providerLabel}{credBadge}</span> });
  /* Evidence, not information: truncated on the row, full and copyable here,
     never wrapping mid-token — this is what gets pasted into a support ticket. */
  if (d.providerMessageId) meta.push({ label: t("messaging.deliveryLog.providerMessageId", "Provider message ID"), value: <span style={{ fontFamily: T.fontMono, fontSize: 12, wordBreak: "break-all" }}>{d.providerMessageId}</span> });
  if (d.replyTo) meta.push({ label: t("messaging.deliveryLog.replyTo", "Reply-to"), value: <span style={{ fontFamily: T.fontMono, fontSize: 12 }}>{d.replyTo}</span> });
  const toneColor: Record<string, string> = { neutral: T.n2, success: T.green1, info: T.blue1, primary: T.p1, warn: T.orange1, danger: T.red1 };
  const toneBg: Record<string, string> = { neutral: T.n6, success: T.green2, info: T.blue2, primary: T.p4, warn: T.orange2, danger: T.red2 };

  /* The whole shell is jrni-ui Drawer now (was a hand-rolled position:fixed
     slide-over). What that BUYS, none of which the hand-roll had: a focus trap,
     aria-modal="true" + aria-labelledby, focus restored to the row on close, and
     the reduced-motion-aware slide. The retired ui-raw note on the close button
     ("IconButton always renders its own border/shadow/background") was stale
     twice over — IconButton grew a borderless `variant="ghost"` on 2026-08-07,
     and Drawer supplies the close affordance itself. The header composition
     (channel glyph + status pill + subject) rides in `title`, which is a
     ReactNode. */
  return (
    <Drawer open onClose={onClose} width={560} side="right"
      aria-label={t("messaging.deliveryLog.deliveryDetails", "Delivery details")}
      title={
        <span style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: alpha(ch.hue, "18"), color: ch.hue, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><ChGlyph id={d.channel} size={17} color={ch.hue} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
              {delivPill(delivStatus(d))}
              <ContentSourceChip row={d} />
              <MissingTokensChip row={d} />
              <span style={{ fontSize: 11, color: T.soft, fontWeight: 400 }}>{d.channel === "email" ? t('messaging.deliveryLog.text') : ch.name}{" "}{delivStatus(d) === "queued"
                ? (d.plannedAt ? <>{t("messaging.deliveryLog.plannedForLower", "· planned")}{" "}{d.plannedAt}</> : <>{t("messaging.deliveryLog.notSentYet", "· not sent yet")}</>)
                : <>{t("messaging.deliveryLog.sent", "· sent")}{" "}{d.sent}</>}</span>
            </span>
            <span style={{ display: "block", fontSize: 16.5, fontWeight: 700, color: T.n1, lineHeight: 1.3 }}>{d.subject || d.text}</span>
          </span>
        </span>
      }
      footer={<Btn size="sm" onClick={onClose}>{t("common.actions.close", "Close")}</Btn>}>
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 22 }}>
            {meta.map((m, i) => (
              <div key={i}>
                <div style={{ ...tx.label, marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 13.5, color: T.n1, fontWeight: 600 }}>{m.value}</div>
                {m.sub ? <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1 }}>{m.sub}</div> : null}
              </div>
            ))}
          </div>

          <div style={{ ...tx.label, marginBottom: 12 }}>{t("messaging.deliveryLog.eventHistory", "Event history")}</div>
          {loading ? (
            <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 24 }}>{t("messaging.deliveryLog.loadingEventHistory", "Loading event history…")}</div>
          ) : (
          <ol style={{ listStyle: "none", margin: "0 0 24px", padding: 0 }}>
            {d.events.map((ev, i) => {
              const e = DELIV_EVENTS[ev.type] || DELIV_EVENTS.send;
              const EI = IconMap[e.icon] || IconMap.send;
              const tone = toneColor[e.tone]; const bg = toneBg[e.tone];
              const last = i === d.events.length - 1;
              return (
                <li key={i} style={{ display: "grid", gridTemplateColumns: "28px 1fr", gap: 12, position: "relative" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <span style={{ width: 28, height: 28, borderRadius: 999, background: bg, color: tone, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${alpha(tone, "33")}` }}>{EI({ size: 13 })}</span>
                    {!last ? <span style={{ width: 2, flex: 1, minHeight: 18, background: T.border, margin: "2px 0" }} /> : null}
                  </div>
                  <div style={{ paddingBottom: last ? 0 : 18 }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{e.label}</span>
                      <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono, whiteSpace: "nowrap" }}>{ev.at}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: T.soft, marginTop: 3, lineHeight: 1.5 }}>{ev.meta || e.blurb}</div>
                    {ev.missingTokens && ev.missingTokens.length && d.contentSource !== "template" ? (
                      /* The names, against the attempt that blanked them — this
                         is what tells an author WHICH token to fix. */
                      <div style={{ marginTop: 6 }}>
                        <div style={{ fontSize: 11.5, color: T.red1, fontWeight: 700, marginBottom: 4 }}>{t("messaging.deliveryLog.renderedEmpty", "Rendered empty for this recipient")}</div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {ev.missingTokens.map((tk) => (
                            <span key={tk} style={{ fontFamily: T.fontMono, fontSize: 11, padding: "2px 6px", borderRadius: 5, background: T.red2, color: T.red1, border: `1px solid ${alpha(T.red1, "33")}` }}>{tk}</span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
          )}

          {d.contentSource === "template" && !loading ? (
            /* Without this sentence an operator sees `{{booking_ref}}` in an old
               send and files a bug. The row is correct; the send predates stored
               content, so what follows is the template as it stands today. */
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", marginBottom: 10, borderRadius: 10, background: T.orange2, border: `1px solid ${alpha(T.orange1, "33")}`, color: T.n1, fontSize: 12.5, lineHeight: 1.5 }}>
              <span style={{ color: T.orange1, flexShrink: 0, display: "inline-flex", marginTop: 1 }}><Icon.info size={14} /></span>
              <span>{t("messaging.deliveryLog.templateRowNote", "This send was made before the platform stored rendered content. What you see is the template as it stands today; tokens are unresolved.")}</span>
            </div>
          ) : null}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={tx.label}>{t("messaging.deliveryLog.message", "Message")}</div>
            {d.channel === "email" && !loading && !d.bodyUnavailable ? (
              <SegmentedControl<"rendered" | "source">
                size="sm"
                ariaLabel="Message body view"
                value={showSource ? "source" : "rendered"}
                onChange={(v) => setShowSource(v === "source")}
                options={[
                  { value: "rendered", label: "Rendered" },
                  { value: "source", label: "HTML source" },
                ]}
              />
            ) : null}
          </div>

          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 10 }}>{t("messaging.deliveryLog.loadingMessage", "Loading message…")}</div>
          ) : d.bodyUnavailable ? (
            <div style={{ padding: 20, border: `1px dashed ${T.n4}`, borderRadius: 10, background: T.n6, color: T.soft, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}><Icon.warn size={14} /> {t("messaging.deliveryLog.messageBodyUnavailableSuppressedOrBouncedSendsArenTStored", "Message body unavailable — suppressed or bounced sends aren't stored.")}</div>
          ) : d.channel === "email" ? (
            showSource ? (
              // ui-raw: fixed dark code-panel palette (#1c1830/#d7d2f0) — an intentional
              // always-dark HTML-source viewer independent of the app's light/dark theme,
              // not a themed surface with a matching T.* token.
              <pre style={{ margin: 0, padding: 14, background: "#1c1830", color: "#d7d2f0", borderRadius: 10, fontSize: 11.5, fontFamily: T.fontMono, lineHeight: 1.5, overflow: "auto", maxHeight: 460, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{d.html}</pre>
            ) : (
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.canvas }}>
                {/* ui-raw: #f4f5f7 is the iframe's own default-white-page background
                    (mirrors an email client's letterbox), not an app surface token. */}
                <iframe title={t("messaging.deliveryLog.emailPreview", "Email preview")} srcDoc={d.html} sandbox="" style={{ width: "100%", height: 480, border: "none", display: "block", background: "#f4f5f7" }} />
              </div>
            )
          ) : (
            <div style={{ background: alpha(ch.hue, "0E"), border: `1px solid ${alpha(ch.hue, "33")}`, borderRadius: 12, padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ width: 26, height: 26, borderRadius: 999, background: alpha(ch.hue, "20"), color: ch.hue, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><ChGlyph id={d.channel} size={13} color={ch.hue} /></span>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.n1 }}>{d.sender.name}</span>
                <span style={{ fontSize: 11, color: T.soft }}>{d.sender.from}</span>
              </div>
              <div style={{ background: T.white, border: `1px solid ${alpha(ch.hue, "26")}`, borderRadius: "4px 14px 14px 14px", padding: "12px 14px", fontSize: 14, lineHeight: 1.55, color: T.n1, maxWidth: 380 }}>{d.text}</div>
            </div>
          )}
        </div>
    </Drawer>
  );
}
